import axios from 'axios'
import { useEffect, useState } from 'react'
import { createApi } from 'unsplash-js'
import packageJson from '../../../package.json'
import { getPublicApiBaseUrl, resolveApiDomainConfig } from './apiDomainConfig'

export const apiURL = getApiServerUrlObject().url
export const publicApiURL = getPublicApiBaseUrl()
export const mediaURL = 'https://media.aumhum.com/images/prod/'
export const baseMediaUrl = 'https://media.aumhum.com/'
export const appVersion = packageJson.version // only do patch updates
// export const appVersion = '9.9.9.9' // max
// Build date is injected at build time via Vite define
export const lastBuildDate = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : 'Build date not available'
export const platformWeb = 'web'
export const appInstallationId = localStorage.getItem('appInstallationId')

function getStoredGeoLocation() {
  try {
    const geoLocationString = localStorage.getItem('geoLocation')
    return geoLocationString ? JSON.parse(geoLocationString) : null
  } catch (error) {
    return null
  }
}

function getAcquisitionReference() {
  const acquisitionReferenceRefId = sessionStorage.getItem('acquisitionReferenceRefId')
  const acquisitionReferenceRefType = sessionStorage.getItem('acquisitionReferenceRefType')

  if (!acquisitionReferenceRefId || !acquisitionReferenceRefType) {
    return null
  }

  return {
    refId: acquisitionReferenceRefId,
    refType: acquisitionReferenceRefType,
  }
}

function getDeviceModel() {
  return navigator?.userAgentData?.platform || navigator?.platform || 'web-browser'
}

export const UNSPLASH_API = createApi({
  // Don't forget to set your access token here!
  // See https://unsplash.com/developers
  accessKey: import.meta.env.VITE_UNSPLASH_ACCESS_KEY,
})

/**
 * Get the main language code (e.g., "en" from "en-GB")
 * Always returns the main code, not the localization variant
 */
export function getMainLanguageCode() {
  const languageCode = sessionStorage.getItem('languageCode')
  if (!languageCode) {
    return (navigator?.language || navigator?.userLanguage || 'en').split('-')[0]
  }
  // Extract main code (before hyphen) if it's a locale variant
  return languageCode.split('-')[0]
}

/**
 * Get country code from localStorage with fallback to "in" (India)
 * Never returns null or undefined - always returns a valid country code
 */
export function getCountryCode() {
  const countryCode = localStorage.getItem('countryCode')
  return countryCode || 'in'
}

export let commonHeadersWeb = {
  'ef-app-version': appVersion,
  'ef-app-platform': platformWeb,
  'ef-app-package': 'com.aumhum.web',
  get ['ef-app-installation-id']() {
    return localStorage.getItem('appInstallationId')
  },
  get ['ef-device-model']() {
    return getDeviceModel()
  },
  get ['ef-device-os-version']() {
    return navigator?.userAgent || 'web'
  },
  'ef-device-manufacturer': 'web',
  get ['ef-user-city']() {
    return getStoredGeoLocation()?.city
  },
  get ['ef-user-lat']() {
    return getStoredGeoLocation()?.lat
  },
  get ['ef-user-lng']() {
    return getStoredGeoLocation()?.lng
  },
  get ['ef-session-id']() {
    return sessionStorage.getItem('sessionId')
  },
  get ['ef-device-os-language']() {
    return (navigator?.language || navigator?.userLanguage || 'en').split('-')[0]
  },
}

export const useFetchPublic = (path, customParams = '', platformType) => {
  const [state, setState] = useState({
    loading: true,
    data: null,
    error: null,
  })

  const [countryCode, setCountryCode] = useState(localStorage.getItem('countryCode') || 'in')
  const [triggerFetch, setTriggerFetch] = useState(false)

  useEffect(() => {
    function handleChangeStorage() {
      const updated = localStorage.getItem('countryCode')
      if (updated) setCountryCode(updated)
    }
    function handleCountryCodeUpdated(e) {
      if (e.detail?.countryCode) setCountryCode(e.detail.countryCode)
    }
    window.addEventListener('storage', handleChangeStorage)
    window.addEventListener('countryCodeUpdated', handleCountryCodeUpdated)
    return () => {
      window.removeEventListener('storage', handleChangeStorage)
      window.removeEventListener('countryCodeUpdated', handleCountryCodeUpdated)
    }
  }, [])

  const mainLanguageCode = getMainLanguageCode()

  useEffect(() => {
    setState({ loading: true, data: null, error: null })

    const effectiveCountryCode = (countryCode || 'in').toLowerCase()
    const paramsWeb = `languageCode=${mainLanguageCode}&platform=${platformWeb}&countryCode=${effectiveCountryCode}`

    let params = paramsWeb

    axios
      .get(`${publicApiURL}${path}?${params}${customParams}`, {
        headers: {
          ...commonHeadersWeb,
          'ef-user-country-code': effectiveCountryCode,
          'ef-app-language': mainLanguageCode,
        },
      })
      .then((res) => {
        setState({ data: res.data, loading: false, error: null })
      })
      .catch((error) => {
        setState({ data: null, loading: false, error: error })
      })
  }, [path, customParams, countryCode, mainLanguageCode, triggerFetch])

  const refreshFunction = () => {
    setTriggerFetch(!triggerFetch)
  }

  return { ...state, refreshFunction }
}

export const useFetch = (path, customParams = '', platformType) => {
  const [state, setState] = useState({
    loading: true,
    data: null,
    error: null,
  })

  const [countryCode, setCountryCode] = useState(localStorage.getItem('countryCode') || 'in')
  const [triggerFetch, setTriggerFetch] = useState(false)

  useEffect(() => {
    function handleChangeStorage() {
      const updated = localStorage.getItem('countryCode')
      if (updated) setCountryCode(updated)
    }
    function handleCountryCodeUpdated(e) {
      if (e.detail?.countryCode) setCountryCode(e.detail.countryCode)
    }
    window.addEventListener('storage', handleChangeStorage)
    window.addEventListener('countryCodeUpdated', handleCountryCodeUpdated)
    return () => {
      window.removeEventListener('storage', handleChangeStorage)
      window.removeEventListener('countryCodeUpdated', handleCountryCodeUpdated)
    }
  }, [])

  const mainLanguageCode = getMainLanguageCode()
  const token = localStorage.getItem('token')

  useEffect(() => {
    setState({ loading: true, data: null, error: null })

    const effectiveCountryCode = (countryCode || 'in').toLowerCase()
    const paramsWeb = `languageCode=${mainLanguageCode}&platform=${platformWeb}&countryCode=${effectiveCountryCode}`

    let params = paramsWeb

    axios
      .get(`${apiURL}${path}?${params}${customParams}`, {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          ...commonHeadersWeb,
          'ef-user-country-code': effectiveCountryCode,
          'ef-app-language': mainLanguageCode,
        },
      })
      .then((res) => {
        setState({ data: res.data, loading: false, error: null })
      })
      .catch((error) => {
        setState({ data: null, loading: false, error: error })
      })
  }, [path, customParams, token, countryCode, mainLanguageCode, triggerFetch])

  const refreshFunction = () => {
    setTriggerFetch(!triggerFetch)
  }

  return { ...state, refreshFunction }
}

export async function verifyLogin(accessToken) {
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()

  const additionalParams = {}

  const acquisitionReferenceRefId = sessionStorage.getItem('acquisitionReferenceRefId')
  const acquisitionReferenceRefType = sessionStorage.getItem('acquisitionReferenceRefType')

  if (acquisitionReferenceRefId) {
    additionalParams['acquisitionReferenceRefId'] = acquisitionReferenceRefId
  }
  if (acquisitionReferenceRefType) {
    additionalParams['acquisitionReferenceRefType'] = acquisitionReferenceRefType
  }

  const queryParams = new URLSearchParams({
    accessToken,
    countryCode,
    languageCode: mainLanguageCode,
    ...additionalParams,
  }).toString()

  try {
    const res = await axios.get(`${apiURL}/api/user/firebase/login?${queryParams}`, {
      headers: {
        ...commonHeadersWeb,
        'ef-user-country-code': countryCode,
        'ef-app-language': mainLanguageCode,
      },
    })
    return res
  } catch (err) {
    console.log(err)
    return err
  }
}

/**
 * Verify a Firebase phone-auth token and create/login the matching mobile user.
 * Uses the mobile-specific server endpoint so the backend resolves usernames by
 * phone number and can attach onboarding acquisition/location metadata.
 */
export async function verifyMobileLogin(accessToken, mobileNumber) {
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()
  const geoLocation = getStoredGeoLocation()
  const acquisitionReference = getAcquisitionReference()

  const body = {
    accessToken,
    mobile: mobileNumber,
    languageCode: mainLanguageCode,
    countryCode,
  }

  if (geoLocation) {
    body.location = {
      ...(geoLocation.lat !== undefined && { lat: geoLocation.lat }),
      ...(geoLocation.lng !== undefined && { lng: geoLocation.lng }),
      ...(geoLocation.city && { city: geoLocation.city }),
      countryCode,
    }
  }

  if (acquisitionReference) {
    body.acquisitionReference = acquisitionReference
  }

  try {
    const res = await axios.post(
      `${apiURL}/api/user/firebase/mobile/login?languageCode=${mainLanguageCode}&platform=${platformWeb}&countryCode=${countryCode}`,
      body,
      {
        headers: {
          ...commonHeadersWeb,
          'Content-Type': 'application/json',
          'ef-user-country-code': countryCode,
          'ef-app-language': mainLanguageCode,
        },
      },
    )
    return res
  } catch (err) {
    console.log(err)
    return err
  }
}

/**
 * Send OTP to WhatsApp number for login
 * @param {string} whatsappNumber - WhatsApp number in E.164 format (e.g., "+919876543210")
 * @returns {Promise} Response with sessionId and otpExpiry
 */
export async function sendWhatsAppOtp(whatsappNumber, options = {}) {
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()

  try {
    const res = await axios.post(
      `${apiURL}/api/user/auth/whatsapp/otp/send`,
      {
        whatsappNumber: whatsappNumber,
        languageCode: mainLanguageCode,
        countryCode: countryCode,
        allowRegistration: Boolean(options.allowRegistration),
        // Optional reCAPTCHA v3 token. Public /signup pages call
        // executeRecaptchaV3('signup_otp_send') and pass the result
        // here. Backend skips verification when no credential is
        // configured for the request's host (dev / preview env), so
        // sending null is harmless on hosts without a site-key.
        ...(options.recaptchaToken ? { recaptchaToken: options.recaptchaToken } : {}),
      },
      {
        headers: {
          ...commonHeadersWeb,
          'Content-Type': 'application/json',
          'ef-user-country-code': countryCode,
          'ef-app-language': mainLanguageCode,
        },
      }
    )
    return res
  } catch (err) {
    console.log(err)
    return err
  }
}

/**
 * Send OTP to email for passwordless login
 * @param {string} email - Registered email address
 * @returns {Promise} Response with sessionId, otpExpiry, retryAfter and resendAvailableAt
 */
export async function sendEmailLoginOtp(email, options = {}) {
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()

  try {
    const res = await axios.post(
      `${apiURL}/api/user/auth/email/otp/send`,
      {
        email,
        languageCode: mainLanguageCode,
        countryCode,
        // When true the backend creates a fresh ROLE_USER if the email
        // is unknown, instead of returning EMAIL_NOT_FOUND. Used by
        // the /signup page (testmode=email) — login flows omit it so
        // the existing "Email address not registered" UX still works.
        allowRegistration: Boolean(options.allowRegistration),
        // Optional reCAPTCHA v3 token — public /signup forwards it,
        // login surfaces leave it null. Backend skips verification on
        // hosts without a configured credential.
        ...(options.recaptchaToken ? { recaptchaToken: options.recaptchaToken } : {}),
      },
      {
        headers: {
          ...commonHeadersWeb,
          'Content-Type': 'application/json',
          'ef-user-country-code': countryCode,
          'ef-app-language': mainLanguageCode,
        },
      }
    )
    return res
  } catch (err) {
    console.log(err)
    return err
  }
}

/**
 * Verify email OTP and get access token
 * @param {string} email - Registered email address
 * @param {string} otp - OTP code
 * @param {string} sessionId - Session ID from sendEmailLoginOtp response
 * @returns {Promise} Response with accessToken, authorities, and accessTokenExpiry
 */
export async function verifyEmailLoginOtp(email, otp, sessionId, options = {}) {
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()

  try {
    const res = await axios.post(
      `${apiURL}/api/user/auth/email/otp/verify`,
      {
        email,
        otp,
        sessionId,
        languageCode: mainLanguageCode,
        countryCode,
        // Same semantics as sendEmailLoginOtp: when true, an unknown
        // email creates a fresh ROLE_USER on verify. The /signup
        // testmode=email path sets it; login flows leave it false.
        allowRegistration: Boolean(options.allowRegistration),
      },
      {
        headers: {
          ...commonHeadersWeb,
          'Content-Type': 'application/json',
          'ef-user-country-code': countryCode,
          'ef-app-language': mainLanguageCode,
        },
      }
    )
    return res
  } catch (err) {
    console.log(err)
    return err
  }
}

/**
 * Verify WhatsApp OTP and get access token
 * @param {string} whatsappNumber - WhatsApp number in E.164 format
 * @param {string} otp - 6-digit OTP code
 * @param {string} sessionId - Session ID from sendWhatsAppOtp response
 * @returns {Promise} Response with accessToken, authorities, and accessTokenExpiry
 */
export async function verifyWhatsAppOtp(whatsappNumber, otp, sessionId, options = {}) {
  const countryCode = getCountryCode()
  const mainLanguageCode = getMainLanguageCode()
  const acquisitionReference = getAcquisitionReference()

  try {
    const res = await axios.post(
      `${apiURL}/api/user/auth/whatsapp/otp/verify`,
      {
        whatsappNumber: whatsappNumber,
        otp: otp,
        sessionId: sessionId,
        languageCode: mainLanguageCode,
        countryCode: countryCode,
        allowRegistration: Boolean(options.allowRegistration),
        ...(acquisitionReference && { acquisitionReference }),
      },
      {
        headers: {
          ...commonHeadersWeb,
          'Content-Type': 'application/json',
          'ef-user-country-code': countryCode,
          'ef-app-language': mainLanguageCode,
        },
      }
    )
    return res
  } catch (err) {
    console.log(err)
    return err
  }
}

export function getApiServerUrlObject() {
  const config = resolveApiDomainConfig()
  return {
    url: config.effectiveApiBaseUrl,
    isCustom: Boolean(config.customApiBaseUrl),
    source: config.apiSource,
    defaultUrl: config.defaultApiBaseUrl,
    publicUrl: config.effectivePublicApiBaseUrl,
    invalidEntries: config.invalidEntries,
  }
}
